/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\ApiKeyQuickListModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Application Keys module.
----------------------------------------------------------------*/


import { Key } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { QuickSearchModal } from '../../../components/ui/QuickSearchModal';
import { useI18n } from '../../../hooks/useI18n';

interface Props {
    isOpen: boolean;
    currentItemId?: string | null;
    items: any[];
    isLoading: boolean;
    searchValue: string;
    onSearchChange: (v: string) => void;
    onClose: () => void;
}

export function ApiKeyQuickListModal({ isOpen, currentItemId, items, isLoading, searchValue, onSearchChange, onClose }: Props) {
    const { t } = useI18n();
    const navigate = useNavigate();

    return (
        <QuickSearchModal
            isOpen={isOpen}
            onClose={onClose}
            title={t('apiKeys.quickList', 'Application Keys')}
            items={items.filter((k): k is typeof k & { id: string } => !!k.id)}
            isLoadingInitial={isLoading}
            isSearching={false}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            onItemSelect={(apiKey) => { onClose(); navigate(`/admin/admin/app-keys/${apiKey.id}`); }}
            currentItemId={currentItemId}
            renderItem={(apiKey, isCurrent) => (
                <div className="flex items-center justify-between">
                    <div>
                        <p className="font-medium text-foreground">{apiKey.name}</p>
                        {apiKey.description && <p className="text-sm text-muted-foreground">{apiKey.description}</p>}
                        {apiKey.keyPrefix && (
                            <p className="text-xs text-muted-foreground">
                                {t('apiKeys.prefix')}: {apiKey.keyPrefix} � {apiKey.isActive ? t('apiKeys.active') : t('apiKeys.inactive')}
                            </p>
                        )}
                    </div>
                    {isCurrent && (
                        <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                            {t('apiKeys.current', 'Current')}
                        </span>
                    )}
                </div>
            )}
            searchPlaceholder={t('apiKeys.quickListSearch', 'Search by name, description or prefix')}
            emptyMessage={t('apiKeys.quickListPrompt', 'Start typing to search...')}
            noResultsMessage={t('apiKeys.quickListNoResults', 'No API keys found')}
            headerIcon={<Key className="h-5 w-5" />}
            minSearchChars={0}
        />
    );
}
